import React, { useState, useMemo } from 'react';
import { AlertTriangle, Receipt } from 'lucide-react';
import { invoicesAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';

export default function BillingConfigTab({ billingConfig, setBillingConfig, allAccounts }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});

  const assetAccounts = useMemo(() => allAccounts.filter(a => a.account_type === 'asset'), [allAccounts]);
  const revenueAccounts = useMemo(() => allAccounts.filter(a => a.account_type === 'revenue'), [allAccounts]);
  const liabilityAccounts = useMemo(() => allAccounts.filter(a => a.account_type === 'liability'), [allAccounts]);
  const [saving, setSaving] = useState(false);

  const startEdit = () => {
    setForm(billingConfig || {});
    setEditing(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await invoicesAPI.updateBillingConfig(form);
      toast.success('Billing configuration saved');
      setBillingConfig({ ...billingConfig, ...form });
      setEditing(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save billing config');
    }
    setSaving(false);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base flex items-center gap-2"><Receipt className="w-5 h-5 text-[#D94E18]" />Invoice Settings</CardTitle>
              <CardDescription>Configure your company details and default accounts for invoicing.</CardDescription>
            </div>
            {!editing && (
              <Button variant="outline" size="sm" onClick={startEdit} data-testid="edit-billing-config-btn">Edit</Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {editing ? (
            <div className="space-y-5">
              <div>
                <p className="text-xs font-bold text-zinc-500 uppercase tracking-wider mb-3">Company Address (for invoices)</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="md:col-span-3">
                    <Label className="text-xs text-zinc-600">Street Address</Label>
                    <Input value={form.company_address || ''} onChange={e => setForm(p => ({...p, company_address: e.target.value}))} className="mt-1" data-testid="billing-address" />
                  </div>
                  <div>
                    <Label className="text-xs text-zinc-600">Postal Code</Label>
                    <Input value={form.company_postal_code || ''} onChange={e => setForm(p => ({...p, company_postal_code: e.target.value}))} className="mt-1" data-testid="billing-postal" />
                  </div>
                  <div>
                    <Label className="text-xs text-zinc-600">City</Label>
                    <Input value={form.company_city || ''} onChange={e => setForm(p => ({...p, company_city: e.target.value}))} className="mt-1" data-testid="billing-city" />
                  </div>
                </div>
              </div>
              <div>
                <p className="text-xs font-bold text-zinc-500 uppercase tracking-wider mb-3">Bank Details</p>
                <div>
                  <Label className="text-xs text-zinc-600">IBAN</Label>
                  <Input value={form.iban || ''} onChange={e => setForm(p => ({...p, iban: e.target.value}))} placeholder="CH93 0076 2011 6238 5295 7" className="mt-1 font-mono" data-testid="billing-iban" />
                </div>
              </div>
              <div>
                <p className="text-xs font-bold text-zinc-500 uppercase tracking-wider mb-3">Default GL Accounts</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs text-zinc-600">Accounts Receivable</Label>
                    <select value={form.default_ar_account_id || ''} onChange={e => setForm(p => ({...p, default_ar_account_id: e.target.value}))} className="mt-1 w-full h-9 rounded-md border border-zinc-200 px-3 text-sm bg-white" data-testid="billing-ar-account">
                      <option value="">Select account...</option>
                      {assetAccounts.map(a => (
                        <option key={a.id} value={a.id}>{a.code} — {a.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs text-zinc-600">Sales Revenue</Label>
                    <select value={form.default_sales_account_id || ''} onChange={e => setForm(p => ({...p, default_sales_account_id: e.target.value}))} className="mt-1 w-full h-9 rounded-md border border-zinc-200 px-3 text-sm bg-white" data-testid="billing-sales-account">
                      <option value="">Select account...</option>
                      {revenueAccounts.map(a => (
                        <option key={a.id} value={a.id}>{a.code} — {a.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs text-zinc-600">VAT Output</Label>
                    <select value={form.default_vat_account_id || ''} onChange={e => setForm(p => ({...p, default_vat_account_id: e.target.value}))} className="mt-1 w-full h-9 rounded-md border border-zinc-200 px-3 text-sm bg-white" data-testid="billing-vat-account">
                      <option value="">Select account...</option>
                      {liabilityAccounts.map(a => (
                        <option key={a.id} value={a.id}>{a.code} — {a.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              <div>
                <p className="text-xs font-bold text-zinc-500 uppercase tracking-wider mb-3">Email Settings</p>
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs text-zinc-600">Sender Email <span className="text-zinc-400">(invoices, notifications)</span></Label>
                    <Input value={form.sender_email || ''} onChange={e => setForm(p => ({...p, sender_email: e.target.value}))} placeholder="kaya@kayaworld.ai" className="mt-1" data-testid="billing-sender-email" />
                  </div>
                  <div>
                    <Label className="text-xs text-zinc-600">Support Email</Label>
                    <Input value={form.support_email || ''} onChange={e => setForm(p => ({...p, support_email: e.target.value}))} placeholder="support@kayaworld.ai" className="mt-1" data-testid="billing-support-email" />
                  </div>
                  <div>
                    <Label className="text-xs text-zinc-600">Marketing & Campaigns</Label>
                    <Input value={form.marketing_email || ''} onChange={e => setForm(p => ({...p, marketing_email: e.target.value}))} placeholder="marketing@kayaworld.ai" className="mt-1" data-testid="billing-marketing-email" />
                  </div>
                  <div>
                    <Label className="text-xs text-zinc-600">Communications</Label>
                    <Input value={form.communications_email || ''} onChange={e => setForm(p => ({...p, communications_email: e.target.value}))} placeholder="comms@kayaworld.ai" className="mt-1" data-testid="billing-comms-email" />
                  </div>
                  <p className="text-[10px] text-zinc-400">All addresses must be from a verified domain in Resend.</p>
                </div>
              </div>
              <div>
                <p className="text-xs font-bold text-zinc-500 uppercase tracking-wider mb-3">Payment Terms</p>
                <div className="flex items-center gap-2 max-w-xs">
                  <Input type="number" min="1" max="365" value={form.payment_terms_days || 30} onChange={e => setForm(p => ({...p, payment_terms_days: parseInt(e.target.value) || 30}))} className="w-20" data-testid="billing-payment-terms" />
                  <span className="text-sm text-zinc-600">days</span>
                </div>
              </div>
              <div className="flex gap-2 pt-2">
                <Button onClick={handleSave} disabled={saving} className="bg-zinc-900 hover:bg-zinc-800 text-white" data-testid="save-billing-config-btn">
                  {saving ? 'Saving...' : 'Save'}
                </Button>
                <Button variant="outline" onClick={() => setEditing(false)}>Cancel</Button>
              </div>
            </div>
          ) : billingConfig ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-3 bg-zinc-50 rounded-lg">
                  <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider mb-1.5">Company Address</p>
                  <p className="text-sm text-zinc-800">{billingConfig.company_address || '—'}</p>
                  <p className="text-sm text-zinc-800">{billingConfig.company_postal_code} {billingConfig.company_city}</p>
                </div>
                <div className="p-3 bg-zinc-50 rounded-lg">
                  <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider mb-1.5">IBAN</p>
                  <p className="text-sm font-mono text-zinc-800">{billingConfig.iban || '—'}</p>
                </div>
              </div>
              <div className="p-3 bg-zinc-50 rounded-lg">
                <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider mb-1.5">Default Accounts</p>
                <div className="grid grid-cols-3 gap-3 text-sm">
                  <div>
                    <span className="text-zinc-500">AR: </span>
                    <span className="font-medium">{allAccounts.find(a => a.id === billingConfig.default_ar_account_id)?.name || '—'}</span>
                  </div>
                  <div>
                    <span className="text-zinc-500">Sales: </span>
                    <span className="font-medium">{allAccounts.find(a => a.id === billingConfig.default_sales_account_id)?.name || '—'}</span>
                  </div>
                  <div>
                    <span className="text-zinc-500">VAT: </span>
                    <span className="font-medium">{allAccounts.find(a => a.id === billingConfig.default_vat_account_id)?.name || '—'}</span>
                  </div>
                </div>
              </div>
              <div className="text-xs text-zinc-500">Sender: <strong>{billingConfig.sender_email || 'Default'}</strong> · Support: <strong>{billingConfig.support_email || 'Not set'}</strong></div>
              <div className="text-xs text-zinc-500">Marketing: <strong>{billingConfig.marketing_email || 'Not set'}</strong> · Comms: <strong>{billingConfig.communications_email || 'Not set'}</strong></div>
              <div className="text-xs text-zinc-500">Payment terms: <strong>{billingConfig.payment_terms_days || 30} days</strong></div>
              {(!billingConfig.default_ar_account_id || !billingConfig.default_sales_account_id) && (
                <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                  <span>Default AR and Sales accounts must be set before creating invoices.</span>
                </div>
              )}
            </div>
          ) : (
            <div className="py-8 text-center text-zinc-400">
              <Receipt className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No billing configuration yet</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
